import { tagName } from "@ember-decorators/component"
import Component from "@ember/component"
import { action, get } from "@ember/object"
import { inject as service } from "@ember/service"
import type { CardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { prepareCourseAssignLessonTemplateToWeek } from "cc-frontend/lib/actions/course/COURSE_ASSIGN_LESSON_TEMPLATE_TO_WEEK"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import type ModalManager from "cc-frontend/services/modal-manager"
import { isCourseDateDefault } from "cc-frontend/utils/filter-dates/course"
import { some } from "lodash-es"
import hasMany from "../../../helpers/has-many"
import styles from "./template-library.module.css"

export interface TemplateLibraryModalArgs {
  cardStack: CardStack
  course: Course
  planbook: Planbook
  modelsNeededForFirstEdit: ModelsNeededForFirstEdit
  rotationCalendar: RotationCalendar
  /**
   * What to do with the template the user picks. The gallery is just a chooser, so a caller who
   * wants something other than "paste it into this lesson" — setting the class's default, say —
   * says so here. Omitted, the paste below runs.
   */
  chooseTemplate?: (template: CardStack) => void
  afterChooseTemplate?: () => void
}
@tagName("")
export default class TemplateLibraryComponent extends Component {
  readonly styles = styles

  // Args
  model!: TemplateLibraryModalArgs
  closeModal!: Function

  // Services
  @service("ember-store") store!: EmberStore
  @service modalManager!: ModalManager
  @service lessonEditor!: LessonEditor
  @service dispatcher!: Dispatcher

  //CPs and getters
  @hasMany("model.planbook", "card-stack", "cardStackTemplates") lessonTemplates

  /**
   * Whether any of this class's default days already carries a template. Read through `get` and
   * lodash rather than off the object: `course` here is the Ember store's copy, so `dates` is a
   * proxied array.
   */
  get courseHasTemplate(): boolean {
    return some(
      get(this.model.course.attributes.calendar, "dates"),
      (date) => isCourseDateDefault(date) && get(date.attributes, "cardStackId") !== null,
    )
  }

  // Actions

  @action
  chooseTemplate(template: CardStack) {
    const chosen = this.model.chooseTemplate
    if (chosen) {
      chosen(template)
      this.closeModal()
      this.model.afterChooseTemplate?.()
      return
    }
    this.pasteTemplate(template)
    this.model.afterChooseTemplate?.()
  }

  pasteTemplate(template) {
    // The first template a class is given becomes the class's template, on every one of its
    // default days — picking one is answering "what do this class's lessons look like", not
    // "what does today look like". Only the first: once some day carries a template, assigning
    // this one to the week would overwrite whatever the other days were set to.
    if (!this.courseHasTemplate) {
      this.dispatcher.dispatchPreparedAction(
        prepareCourseAssignLessonTemplateToWeek({
          course: this.model.course,
          cardStackId: template.id,
          planbookId: this.model.planbook.id,
          rotationCalendar: this.model.rotationCalendar,
        }),
      )
    }

    // pasteCardStack clones the template (remapping card ids) and keeps the target lesson's title.
    this.lessonEditor.pasteCardStack(
      this.model.modelsNeededForFirstEdit,
      this.model.cardStack.id,
      this.model.course,
      this.model.planbook,
      template.attributes,
      this.model.cardStack.attributes,
      "template",
      {
        id: template.id,
        planbookId: this.model.planbook.id,
        courseId: null,
        // I think this is right -- we should really get the ownerIds from the planbook that's referenced in the card stack
        ownerIds: [this.model.planbook.relationships.owner.data.id],
        version: template.attributes.version,
        source: "template",
      },
    )
    this.closeModal()
  }
}
